import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  IconDashboard,
  IconClients,
  IconWallet,
  IconSoberLiving,
  IconLogout,
  IconChild,
  IconPf25Admins,
  IconManager,
  IconSmallArrow,
  IconNoRelation,
  IconSearch,
  IconDoer,
} from 'components';
import classNames from 'classnames';
import { useAuth } from 'context/authContext';
import { Input, Radio } from 'antd';
import Logo from 'assets/images/logo.svg';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { getIdFromPath, isInRoute } from 'utils/helpers';
import { ROLE_TYPES, SLH_TYPES } from 'api/constants';
import { useUser } from 'context/userContext';
import { COLORS } from 'utils/colors';
import { IconResetArrows } from 'components/icons/ResetArrows';

export const SideMenu = () => {
  const {
    role,
    logout,
    partnerIDSetter,
    partnerId,
    parentId,
    parentIDSetter,
    partnerChilds,
    setPartnerChildren,
    isParentManaging,
  } = useAuth();
  const { userDetails } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const userID = getIdFromPath(location.pathname);
  const [childs, setChilds] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const sorted = Array.isArray(partnerChilds)
      ? [...partnerChilds].sort((a, b) => String(a.name).localeCompare(String(b.name)))
      : [];
    setChilds(sorted);
  }, [partnerChilds]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (userDetails?.partner_details?.type === SLH_TYPES.parent) {
      parentIDSetter(userDetails?.partner_details?.id);
      setPartnerChildren(userDetails?.partner_details?.children);
    }
  }, [parentIDSetter, setPartnerChildren, userDetails?.partner_details]);

  const handlePartnerIdChange = (e) => {
    const selectedId = e.target.value;
    partnerIDSetter(selectedId);
    setIsDropdownOpen(false);
  };

  const onSearch = (e) => {
    const filteredChilds = partnerChilds.filter((child) =>
      child.name.toLowerCase().includes(e.target.value.toLowerCase()),
    );
    setChilds(filteredChilds);
  };

  return (
    <div className="flex flex-col base-block w-full max-w-[306px] h-[calc(100vh-80px)] sticky top-10 z-[100]">
      <img
        onClick={() => navigate('/dashboard')}
        className="w-48 mb-6 pt-7 ml-7 cursor-pointer"
        src={Logo}
        alt="pw25 logo"
      />
      {role === ROLE_TYPES.slh_manager && userDetails && (
        <div
          ref={
            userDetails?.partner_details?.type === SLH_TYPES.parent ||
            (parentId && partnerId !== parentId)
              ? dropdownRef
              : null
          }
          className={`flex items-center p-2 w-full mb-5 gap-x-2 relative px-4 h-14 ${
            isParentManaging ? 'bg-yellow' : ''
          }`}
        >
          {userDetails?.partner_details?.type === SLH_TYPES.parent ||
          (parentId && partnerId !== parentId) ? (
            <>
              <div
                className="flex items-center justify-between flex-1 gap-x-2 cursor-pointer"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className="flex items-center gap-x-2">
                  {+partnerId === +parentId ? <IconSoberLiving /> : <IconChild />}
                  <div className="flex flex-col flex-1">
                    <b className="text-14 mb-0.5">{userDetails?.partner_name}</b>
                    <p className="text-12 font-normal text-lightBlue">
                      {userDetails?.partner_address}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-x-2">
                  {+parentId !== +partnerId && (
                    <IconResetArrows
                      className="ml-auto"
                      onClick={(e) => {
                        partnerIDSetter(parentId);
                        setIsDropdownOpen(false);
                        e.stopPropagation();
                      }}
                    />
                  )}
                  <IconSmallArrow color={COLORS.black} className="rotate-[-180deg] w-3 h-3" />
                </div>
              </div>
              <div
                className={`flex flex-col rounded-xl bg-white shadow-block-shadow p-4 gap-y-2 w-[450px] absolute left-[calc(100%+16px)] top-0 ${
                  isDropdownOpen ? 'block' : 'hidden'
                }`}
              >
                <p className="text-14 font-medium">View Data For</p>
                <Input
                  className="search text-lightBlue text-16 rounded-ten bg-light"
                  type="text"
                  placeholder="Search"
                  addonBefore={<IconSearch color={COLORS.lightBlue} />}
                  onChange={onSearch}
                />
                <div className="flex flex-col bg-light border border-divider rounded-lg max-h-[450px] overflow-y-auto">
                  <Radio.Group
                    onChange={handlePartnerIdChange}
                    className="w-full flex flex-col gap-x-4"
                    value={+parentId === +partnerId ? +parentId : +partnerId}
                  >
                    <Radio
                      className={`relative px-4 py-1.5 m-0 flex-1 flex items-center rounded-lg hover:bg-lightBlue ${partnerId === parentId ? '!bg-white border border-orange' : ''}`}
                      value={parentId}
                    >
                      <div className="flex items-center">
                        <IconSoberLiving className="mr-2" />
                        <p className="text-sm whitespace-nowrap">
                          {userDetails?.partner_details?.parent
                            ? userDetails?.partner_details?.parent.name
                            : userDetails?.partner_details?.name}
                        </p>
                        {userDetails?.partner_details?.doer && (
                          <IconDoer className="absolute top-1 right-1 ml-2" />
                        )}
                      </div>
                    </Radio>
                    {childs?.length > 0 &&
                      Array.isArray(childs) &&
                      childs?.map((child) => (
                        <Radio
                          key={child.id}
                          className={`relative ml-4 px-4 py-2 m-0 flex-1 flex items-center rounded-lg hover:bg-lightBlue ${partnerId === child.id ? '!bg-white border border-orange' : ''}`}
                          value={child.id}
                        >
                          <div className="flex items-center">
                            <IconChild className="mr-2" />
                            <p className="text-sm whitespace-nowrap">{child.name}</p>
                            {child.doer && <IconDoer className="absolute top-1 right-1 ml-2" />}
                          </div>
                        </Radio>
                      ))}
                  </Radio.Group>
                </div>
              </div>
            </>
          ) : (
            <>
              {userDetails?.partner_details?.type === SLH_TYPES.child ? (
                <IconChild />
              ) : (
                <IconNoRelation />
              )}
              <div className="flex flex-col flex-1">
                <b className="text-14 mb-0.5">{userDetails?.partner_name}</b>
                <p className="text-12 font-normal text-lightBlue">{userDetails?.partner_address}</p>
              </div>
            </>
          )}
        </div>
      )}
      <nav
        className={classNames(
          'flex flex-1 relative border-b border-t border-divider',
          'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
          'after:content-[""] after:absolute after:w-4 after:h-4 after:rounded-full after:border after:border-divider after:bg-white after:z-10 after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:translate-y-1/2',
        )}
      >
        <div className="flex flex-col gap-y-4 w-full overflow-hidden mt-10">
          <Link
            className="flex items-center py-3 pr-3 duration-150 group hover:bg-yellow"
            to={`/dashboard`}
          >
            <span
              className={`w-6 h-6 rounded-full border border-lightBlue -translate-x-1/2 group-hover:border-black group-hover:bg-black ${location.pathname.includes('dashboard') ? 'bg-orange border-orange' : 'bg-transparent'}`}
            />
            <IconDashboard
              className="mr-3 duration-150 group-hover:[&_path]:stroke-black [&_path]:duration-150"
              color={isInRoute(location.pathname, 'dashboard') ? 'orange' : 'black'}
            />
            <p
              className={`text-sm leading-[21px] duration-150 group-hover:text-black ${isInRoute(location.pathname, 'dashboard') ? 'text-orange' : 'text-black'}`}
            >
              Dashboard
            </p>
          </Link>
          <Link
            className="flex items-center py-3 pr-3 duration-150 group hover:bg-yellow"
            to={`/clients`}
          >
            <span
              className={`w-6 h-6 rounded-full border border-lightBlue -translate-x-1/2 group-hover:border-black group-hover:bg-black ${isInRoute(location.pathname, 'client') ? 'bg-orange border-orange' : 'bg-transparent'}`}
            />
            <IconClients
              className="mr-3 group-hover:[&_path]:stroke-black [&_path]:duration-150"
              color={isInRoute(location.pathname, 'client') ? 'orange' : 'black'}
            />
            <p
              className={`text-sm leading-[21px] duration-150 group-hover:text-black ${isInRoute(location.pathname, 'client') ? 'text-orange' : 'text-black'}`}
            >
              Residents
            </p>
          </Link>
          {role === ROLE_TYPES.admin && (
            <Link
              className="flex items-center py-3 pr-3 duration-150 group hover:bg-yellow"
              to="/partners"
            >
              <span
                className={`w-6 h-6 rounded-full border border-lightBlue -translate-x-1/2 group-hover:border-black group-hover:bg-black ${isInRoute(location.pathname, 'partner') ? 'bg-orange border-orange' : 'bg-transparent'}`}
              />
              <IconSoberLiving
                className="mr-3 group-hover:[&_path]:stroke-black [&_path]:duration-150"
                color={isInRoute(location.pathname, 'partner') ? 'orange' : 'black'}
              />
              <p
                className={`text-sm leading-[21px] duration-150 group-hover:text-black ${isInRoute(location.pathname, 'partner') ? 'text-orange' : 'text-black'}`}
              >
                Partners (SLH)
              </p>
            </Link>
          )}
          {(role === ROLE_TYPES.slh_manager || role === ROLE_TYPES.admin) && (
            <Link
              className="flex items-center py-3 pr-3 duration-150 group hover:bg-yellow"
              to={'/slh-managers'}
            >
              <span
                className={`w-6 h-6 rounded-full border border-lightBlue -translate-x-1/2 group-hover:border-black group-hover:bg-black ${isInRoute(location.pathname, 'slh-managers') || isInRoute(location.pathname, 'slh-manager-profile') ? 'bg-orange border-orange' : 'bg-transparent'}`}
              />
              <IconManager
                className="mr-3 group-hover:[&_path]:stroke-black [&_path]:duration-150"
                color={
                  isInRoute(location.pathname, 'slh-managers') ||
                  isInRoute(location.pathname, 'slh-manager-profile')
                    ? 'orange'
                    : 'black'
                }
              />
              <p
                className={`text-sm leading-[21px] duration-150 group-hover:text-black ${isInRoute(location.pathname, 'slh-managers') || isInRoute(location.pathname, 'slh-manager-profile') ? 'text-orange' : 'text-black'}`}
              >
                SLH Managers
              </p>
            </Link>
          )}
          {role === ROLE_TYPES.admin && (
            <Link
              className="flex items-center py-3 pr-3 duration-150 group hover:bg-yellow"
              to="/pf25-admins"
            >
              <span
                className={`w-6 h-6 rounded-full border border-lightBlue -translate-x-1/2 group-hover:border-black group-hover:bg-black ${isInRoute(location.pathname, 'pf25-admins') || isInRoute(location.pathname, 'admin-profile') ? 'bg-orange border-orange' : 'bg-transparent'}`}
              />
              <IconPf25Admins
                className="mr-3 group-hover:[&_path]:stroke-black [&_path]:duration-150"
                color={
                  isInRoute(location.pathname, 'pf25-admins') ||
                  isInRoute(location.pathname, 'admin-profile')
                    ? 'orange'
                    : 'black'
                }
              />
              <p
                className={`text-sm leading-[21px] duration-150 group-hover:text-black ${isInRoute(location.pathname, 'pf25-admins') || isInRoute(location.pathname, 'admin-profile') ? 'text-orange' : 'text-black'}`}
              >
                PF25 Admins
              </p>
            </Link>
          )}
          {userDetails?.partner_details?.type === SLH_TYPES.parent && !isParentManaging && (
            <Link
              className="flex items-center py-3 pr-3 duration-150 group hover:bg-yellow"
              to="/slh-children"
            >
              <span
                className={`w-6 h-6 rounded-full border border-lightBlue -translate-x-1/2 group-hover:border-black group-hover:bg-black ${isInRoute(location.pathname, 'slh-children') || isInRoute(location.pathname, 'partner/view') ? 'bg-orange border-orange' : 'bg-transparent'}`}
              />
              <IconChild
                className="mr-3 group-hover:[&_path]:stroke-black [&_path]:duration-150"
                color={
                  isInRoute(location.pathname, 'slh-children') ||
                  isInRoute(location.pathname, 'partner/view')
                    ? 'orange'
                    : 'black'
                }
              />
              <p
                className={`text-sm leading-[21px] duration-150 group-hover:text-black ${isInRoute(location.pathname, 'slh-children') || isInRoute(location.pathname, 'partner/view') ? 'text-orange' : 'text-black'}`}
              >
                Child SLH
              </p>
            </Link>
          )}
          <Link
            className="flex items-center py-3 pr-3 duration-150 group hover:bg-yellow"
            to="/finances"
          >
            <span
              className={`w-6 h-6 rounded-full border border-lightBlue -translate-x-1/2 group-hover:border-black group-hover:bg-black ${isInRoute(location.pathname, 'finance') ? 'bg-orange border-orange' : 'bg-transparent'}`}
            />
            <IconWallet
              className="mr-3 group-hover:[&_path]:stroke-black [&_path]:duration-150"
              color={isInRoute(location.pathname, 'finance') ? 'orange' : 'black'}
            />
            <p
              className={`text-sm leading-[21px] duration-150 group-hover:text-black ${isInRoute(location.pathname, 'finance') ? 'text-orange' : 'text-black'}`}
            >
              Finances
            </p>
          </Link>
        </div>
      </nav>
      <div className="flex justify-between items-center pt-10 pb-8 px-7">
        <div
          className={`flex cursor-pointer p-2 pr-4 rounded-3xl ${userID === userDetails?.user_id ? 'bg-yellow' : ''}`}
          onClick={() =>
            navigate(
              `/${role === ROLE_TYPES.slh_manager ? 'slh-manager-profile' : 'admin-profile'}/${userDetails?.user_id}`,
            )
          }
        >
          <img
            className="w-[42px] h-[42px] rounded-full"
            src={userDetails?.avatar ? userDetails?.avatar : DefaultAvatar}
            alt="profile"
          />
          <div className="flex flex-col ml-3">
            <p className="text-black text-sm">
              {userDetails?.firstname} {userDetails?.lastname}
            </p>
            <p className="text-lightBlue text-xs font-normal capitalize">
              {role?.replace('_', ' ')}
            </p>
          </div>
        </div>
        <IconLogout className="cursor-pointer" onClick={logout} />
      </div>
    </div>
  );
};
